import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Plus, Shield, Pencil, Trash2, Lock, ChevronRight, Users, KeyRound, Search, X, LayoutDashboard } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export default function RolesPage() {
  const { t } = useTranslation();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', permissions: [] });
  const [searchFilter, setSearchFilter] = useState('');
  const [savingKey, setSavingKey] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.get('/api/roles'),
        api.get('/api/permissions'),
      ]);
      setRoles(rolesRes.data);
      setPermissions(permsRes.data);
    } catch { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const permsByCategory = permissions.reduce((acc, p) => {
    if (!acc[p.category]) acc[p.category] = [];
    acc[p.category].push(p);
    return acc;
  }, {});

  const togglePerm = (key) => {
    setForm(prev => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter(p => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const selectAllCategory = (category) => {
    const catKeys = permsByCategory[category].map(p => p.key);
    const allSelected = catKeys.every(k => form.permissions.includes(k));
    if (allSelected) {
      setForm(prev => ({ ...prev, permissions: prev.permissions.filter(p => !catKeys.includes(p)) }));
    } else {
      setForm(prev => ({ ...prev, permissions: [...new Set([...prev.permissions, ...catKeys])] }));
    }
  };

  const handleCreate = async () => {
    if (!form.name.trim()) return toast.error('Role name is required');
    try {
      await api.post('/api/roles', form);
      toast.success('Role created');
      setCreateOpen(false);
      setForm({ name: '', description: '', permissions: [] });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  // Live permission toggle — saves immediately, reverts on failure
  const toggleLivePerm = async (key) => {
    const current = selectedRole.permissions || [];
    const isOn = current.includes(key);
    if (isOn && key === 'manage_roles' && selectedRole.key === 'admin') {
      toast.error('Manage Roles cannot be removed from the Admin role — you would lock yourself out of this page.');
      return;
    }
    const next = isOn ? current.filter(k => k !== key) : [...current, key];
    setSelectedRole(r => ({ ...r, permissions: next }));
    setRoles(rs => rs.map(r => (r.id === selectedRole.id ? { ...r, permissions: next } : r)));
    setSavingKey(key);
    try {
      await api.put(`/api/roles/${selectedRole.id}`, { permissions: next });
    } catch (e) {
      setSelectedRole(r => ({ ...r, permissions: current }));
      setRoles(rs => rs.map(r => (r.id === selectedRole.id ? { ...r, permissions: current } : r)));
      toast.error(e.response?.data?.detail || 'Failed to save permission');
    } finally {
      setSavingKey(null);
    }
  };

  const handleUpdate = async () => {
    if (!form.name.trim()) return toast.error('Role name is required');
    try {
      await api.put(`/api/roles/${selectedRole.id}`, { name: form.name, description: form.description });
      toast.success('Role updated');
      setEditMode(false);
      const updated = { ...selectedRole, name: form.name, description: form.description };
      setSelectedRole(updated);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this role? Users assigned to it will lose their permissions.')) return;
    try {
      await api.delete(`/api/roles/${id}`);
      toast.success('Role deleted');
      if (selectedRole?.id === id) { setSelectedRole(null); setEditMode(false); }
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const openRoleDetail = (role) => {
    setSelectedRole(role);
    setEditMode(false);
    setForm({ name: role.name, description: role.description || '', permissions: [...role.permissions] });
  };

  const startEdit = () => {
    setForm({ name: selectedRole.name, description: selectedRole.description || '', permissions: [...selectedRole.permissions] });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setForm({ name: selectedRole.name, description: selectedRole.description || '', permissions: [...selectedRole.permissions] });
  };

  // Category icon mapping
  const categoryIcon = (cat) => {
    const map = {
      'Users & Access': Users,
      'Sponsors': Shield,
      'Rights': KeyRound,
      'Dashboard': LayoutDashboard,
    };
    return map[cat] || Shield;
  };

  // Filtered permissions for search
  const filterPerms = (perms) => {
    if (!searchFilter) return perms;
    return perms.filter(p =>
      p.name.toLowerCase().includes(searchFilter.toLowerCase()) ||
      p.key.toLowerCase().includes(searchFilter.toLowerCase()) ||
      (p.category || '').toLowerCase().includes(searchFilter.toLowerCase())
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading...</div>;

  return (
    <TooltipProvider delayDuration={200}>
      <div data-testid="roles-page" className="space-y-0">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{t('roles_permissions') || 'Roles & Permissions'}</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{t('roles_desc') || 'Manage user roles and their platform permissions'}</p>
          </div>
          <Button
            data-testid="create-role-btn"
            size="sm"
            onClick={() => {
              setForm({ name: '', description: '', permissions: [] });
              setCreateOpen(true);
            }}
            className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-xs h-9 px-4"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />Create Role
          </Button>
        </div>

        {/* Main content: list + detail panel */}
 <div className="flex gap-0 rounded-card shadow-card bg-white dark:bg-gray-900 min-h-[520px]">
          {/* Left: Role list */}
          <div className={`${selectedRole ? 'w-[340px] min-w-[340px] border-r border-gray-200 dark:border-gray-700' : 'w-full'} transition-all duration-200`}>
            <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
              <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">{roles.length} Role{roles.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {roles.map(role => {
                const isActive = selectedRole?.id === role.id;
                return (
                  <div
                    key={role.id}
                    data-testid={`role-card-${role.key || role.id}`}
                    onClick={() => openRoleDetail(role)}
                    className={`flex items-center gap-3 px-4 py-3.5 cursor-pointer transition-colors ${
                      isActive
                        ? 'bg-gray-50 dark:bg-gray-800/70 border-l-[3px] border-l-gray-900 dark:border-l-white'
                        : 'border-l-[3px] border-l-transparent hover:bg-gray-50/60 dark:hover:bg-gray-800/40'
                    }`}
                  >
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${
                      role.is_system
                        ? 'bg-amber-50 dark:bg-amber-950/40'
                        : 'bg-gray-100 dark:bg-gray-800'
                    }`}>
                      {role.is_system
                        ? <Lock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                        : <Shield className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="section-title truncate">{role.name}</span>
                        {role.is_system && (
                          <Badge variant="outline" className="text-[9px] font-medium px-1.5 py-0 h-4 border-amber-300 text-amber-600 dark:border-amber-700 dark:text-amber-400 shrink-0">
                            Built-in
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 dark:text-gray-500 truncate mt-0.5">{role.description || 'No description'}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-[11px] font-medium text-gray-400 dark:text-gray-500 tabular-nums">{role.permissions?.length || 0}</span>
                      <ChevronRight className={`h-4 w-4 text-gray-300 dark:text-gray-600 transition-transform ${isActive ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Detail panel */}
          {selectedRole && (
            <div className="flex-1 flex flex-col min-w-0">
              {/* Detail header */}
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${
                    selectedRole.is_system ? 'bg-amber-50 dark:bg-amber-950/40' : 'bg-gray-100 dark:bg-gray-800'
                  }`}>
                    {selectedRole.is_system
                      ? <Lock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                      : <Shield className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                    }
                  </div>
                  <div className="min-w-0">
                    {editMode ? (
                      <Input
                        data-testid="edit-role-name"
                        value={form.name}
                        onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                        disabled={selectedRole.is_system}
                        className="h-8 text-sm font-semibold bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                        placeholder="Role name"
                      />
                    ) : (
                      <h2 className="text-base font-bold text-gray-900 dark:text-white truncate">{selectedRole.name}</h2>
                    )}
                    {editMode ? (
                      <Input
                        data-testid="edit-role-desc"
                        value={form.description}
                        onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                        className="h-7 text-xs mt-1 bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700"
                        placeholder="Description"
                      />
                    ) : (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate">{selectedRole.description || 'No description'}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {editMode ? (
                    <>
                      <Button variant="ghost" size="sm" onClick={cancelEdit} className="text-xs h-8 text-gray-500">Cancel</Button>
                      <Button data-testid="update-role-btn" size="sm" onClick={handleUpdate} className="text-xs h-8 bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100">Save Changes</Button>
                    </>
                  ) : (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button data-testid={`edit-role-${selectedRole.id}`} variant="outline" size="icon" className="h-8 w-8 border-gray-200 dark:border-gray-700" onClick={startEdit}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit role</TooltipContent>
                      </Tooltip>
                      {!selectedRole.is_system && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button data-testid={`delete-role-${selectedRole.id}`} variant="outline" size="icon" className="h-8 w-8 border-red-200 dark:border-red-800 text-red-500 hover:bg-red-50 dark:hover:bg-red-950" onClick={() => handleDelete(selectedRole.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Delete role</TooltipContent>
                        </Tooltip>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 lg:hidden" onClick={() => setSelectedRole(null)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Permissions stats bar */}
              <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-800 flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-gray-400" />
                  <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    {selectedRole.permissions?.length || 0}
                  </span>
                  <span className="text-xs text-gray-400">of {permissions.length} permissions</span>
                </div>

                <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gray-900 dark:bg-white rounded-full transition-all duration-300"
                    style={{ width: `${((selectedRole.permissions?.length || 0) / Math.max(permissions.length, 1)) * 100}%` }}
                  />
                </div>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                  <Input
                    data-testid="perm-search-input"
                    value={searchFilter}
                    onChange={e => setSearchFilter(e.target.value)}
                    className="h-7 w-44 pl-8 text-xs bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    placeholder="Filter permissions..."
                  />
                </div>
              </div>

              {/* Permissions grid */}
              <ScrollArea className="flex-1">
                <div className="px-6 py-4 space-y-4">
                  {Object.entries(permsByCategory).map(([category, perms]) => {
                    const filtered = filterPerms(perms);
                    if (filtered.length === 0) return null;
                    const catKeys = perms.map(p => p.key);
                    const activePerms = selectedRole.permissions || [];
                    const selectedCount = catKeys.filter(k => activePerms.includes(k)).length;

                    return (
                      <div key={category} className="border border-gray-150 dark:border-gray-700/70 rounded-lg overflow-hidden">
                        {/* Category header */}
                        <div className="flex items-center justify-between px-4 py-3 bg-gray-100 dark:bg-gray-800 border-l-[3px] border-l-gray-900 dark:border-l-white">
                          <div className="flex items-center gap-2.5">
                            {React.createElement(categoryIcon(category), { className: 'h-4 w-4 text-gray-700 dark:text-white shrink-0' })}
                            <span className="text-[13px] font-bold text-gray-900 dark:text-white uppercase tracking-wide">{category}</span>
                            <Badge variant="secondary" className="text-[10px] h-[18px] px-1.5 font-semibold tabular-nums bg-gray-900/10 dark:bg-white/15 text-gray-700 dark:text-white pointer-events-none select-none">
                              {selectedCount}/{catKeys.length}
                            </Badge>
                          </div>
                        </div>
                        <Separator />
                        {/* Permission rows - compact 2-column grid, live toggles */}
                        <div className="grid grid-cols-2 gap-0">
                          {filtered.map((p, idx) => {
                            const isEnabled = activePerms.includes(p.key);
                            const labelCls = isEnabled
                              ? 'text-gray-900 dark:text-white font-semibold'
                              : 'text-gray-400 dark:text-gray-500 font-light';
                            return (
                              <div
                                key={p.key}
                                data-testid={`perm-${p.key}`}
                                className={`flex items-center justify-between px-3 py-2 transition-colors border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50/50 dark:hover:bg-gray-800/30 ${idx % 2 === 0 ? 'border-r border-r-gray-100 dark:border-r-gray-800' : ''} ${savingKey === p.key ? 'opacity-60' : ''}`}
                                onClick={() => toggleLivePerm(p.key)}
                              >
                                <div className="flex-1 min-w-0 pr-2">
                                  <span className={`text-xs ${labelCls}`}>{p.name}</span>
                                </div>
                                <Switch
                                  data-testid={`perm-switch-${p.key}`}
                                  checked={isEnabled}
                                  onCheckedChange={() => toggleLivePerm(p.key)}
                                  onClick={e => e.stopPropagation()}
                                  className="shrink-0 scale-90 data-[state=checked]:bg-emerald-500 dark:data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-gray-300 dark:data-[state=unchecked]:bg-gray-700"
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Empty state */}
          {!selectedRole && roles.length > 0 && (
            <div className="hidden" />
          )}
        </div>

        {/* Create Role Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="bg-white dark:bg-gray-900 max-w-2xl max-h-[85vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg">
                <div className="h-8 w-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  <Shield className="h-4 w-4 text-gray-500" />
                </div>
                {t('create_role') || 'Create New Role'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-5 pt-2">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">{t('role_name') || 'Role Name'}</Label>
                  <Input
                    data-testid="role-name-input"
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    placeholder="e.g. Junior Manager"
                    className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                  />
                </div>
                <div>
                  <Label className="text-xs text-gray-500 dark:text-gray-400 mb-1.5 block">{t('description') || 'Description'}</Label>
                  <Input
                    data-testid="role-desc-input"
                    value={form.description}
                    onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description of this role"
                    className="bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                  />
                </div>
              </div>
              <Separator />
              <div>
                <div className="flex items-center justify-between mb-3">
                  <Label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('permissions') || 'Permissions'} ({form.permissions.length}/{permissions.length})</Label>
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                    <Input
                      data-testid="create-perm-search"
                      value={searchFilter}
                      onChange={e => setSearchFilter(e.target.value)}
                      className="h-7 w-44 pl-8 text-xs bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                      placeholder="Filter..."
                    />
                  </div>
                </div>
                <ScrollArea className="h-[360px] pr-1">
                  <div className="space-y-3">
                    {Object.entries(permsByCategory).map(([category, perms]) => {
                      const filtered = filterPerms(perms);
                      if (filtered.length === 0) return null;
                      const catKeys = perms.map(p => p.key);
                      const allSelected = catKeys.every(k => form.permissions.includes(k));

                      return (
                        <div key={category} className="border border-gray-150 dark:border-gray-700/70 rounded-lg overflow-hidden">
                          <div
                            className="flex items-center justify-between px-4 py-2 bg-gray-50/80 dark:bg-gray-800/50 cursor-pointer hover:bg-gray-100/80 dark:hover:bg-gray-800"
                            onClick={() => selectAllCategory(category)}
                          >
                            <div className="flex items-center gap-2.5">
                              <span className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{category}</span>
                              <Badge variant="secondary" className="text-[10px] h-[18px] px-1.5 font-medium tabular-nums bg-gray-200/70 dark:bg-gray-700 text-gray-600 dark:text-gray-300 pointer-events-none select-none">
                                {catKeys.filter(k => form.permissions.includes(k)).length}/{catKeys.length}
                              </Badge>
                            </div>
                            <Switch
                              data-testid={`create-cat-toggle-${category.replace(/\s+/g, '-').toLowerCase()}`}
                              checked={allSelected}
                              onCheckedChange={() => selectAllCategory(category)}
                              className="scale-90"
                            />
                          </div>
                          <Separator />
                          <div className="grid grid-cols-2 gap-0">
                            {filtered.map((p, idx) => (
                              <div
                                key={p.key}
                                className={`flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50/50 dark:hover:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800 ${idx % 2 === 0 ? 'border-r border-r-gray-100 dark:border-r-gray-800' : ''}`}
                                onClick={() => togglePerm(p.key)}
                              >
                                <span className={`text-xs flex-1 min-w-0 pr-2 ${form.permissions.includes(p.key) ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400'}`}>{p.name}</span>
                                <Switch
                                  checked={form.permissions.includes(p.key)}
                                  onCheckedChange={() => togglePerm(p.key)}
                                  className="shrink-0 scale-90"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </div>
            </div>
            <DialogFooter className="border-t border-gray-100 dark:border-gray-800 pt-4">
              <Button variant="outline" onClick={() => setCreateOpen(false)} className="border-gray-200 dark:border-gray-700 text-xs">Cancel</Button>
              <Button data-testid="save-role-btn" onClick={handleCreate} className="bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100 text-xs">Create Role</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
